import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Loader2, CheckCircle2, Upload, X, Image as ImageIcon, Palette,
  Info, ShieldCheck, Link2, MapPin, Clock, Timer, Star,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const DEFAULT_BADGES = { instant_confirmation: true, secure_payment: true, mobile_qr: true };

export default function BookingEngineSettings({ company, onSaved }) {
  const [f, setF] = useState({
    hero_image_url: company.hero_image_url || '',
    hero_headline: company.hero_headline || company.name || '',
    hero_subtext: company.hero_subtext || company.description || '',
    gallery_urls: Array.isArray(company.gallery_urls) ? company.gallery_urls : [],
    gallery_enabled: company.gallery_enabled !== false,
    gallery_mobile_layout: company.gallery_mobile_layout || 'carousel',
    location: company.location || company.address || '',
    opening_hours: company.opening_hours || '',
    visit_duration: company.visit_duration || '',
    trust_badges: { ...DEFAULT_BADGES, ...(company.trust_badges || {}) },
    footer_privacy_url: company.footer_privacy_url || '',
    footer_terms_url: company.footer_terms_url || '',
    support_email: company.support_email || company.email || '',
    support_phone: company.support_phone || company.contact_number || '',
    google_rating: company.google_rating || '',
    google_review_count: company.google_review_count || '',
    google_maps_url: company.google_maps_url || '',
    ticket_seller_name: company.ticket_seller_name || '',
    ticket_seller_reg_no: company.ticket_seller_reg_no || '',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(null); // 'hero' | 'gallery' | null
  const heroRef = useRef();
  const galleryRef = useRef();
  const fallbackHero = company.image_url || '';

  const setKey = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const uploadImage = async (fileObj, slot) => {
    if (!fileObj) return null;
    if (fileObj.size > 8 * 1024 * 1024) { setMsg('Image too large. Max 8MB.'); return null; }
    const fd = new FormData(); fd.append('file', fileObj);
    setUploading(slot); setMsg('');
    try {
      const { data } = await axios.post(`${API}/api/eticket/company/upload-image?slot=${slot}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return `${API}${data.url}`;
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Upload failed'); return null;
    } finally { setUploading(null); }
  };

  const onHeroPicked = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const url = await uploadImage(file, 'hero');
    if (url) setF((s) => ({ ...s, hero_image_url: url }));
    if (heroRef.current) heroRef.current.value = '';
  };

  const onGalleryPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (f.gallery_urls.length + files.length > 10) { setMsg('Max 10 gallery images.'); return; }
    const uploaded = [];
    for (const file of files) {
      const url = await uploadImage(file, 'gallery');
      if (url) uploaded.push(url);
    }
    setF((s) => ({ ...s, gallery_urls: [...s.gallery_urls, ...uploaded] }));
    if (galleryRef.current) galleryRef.current.value = '';
  };

  const removeGalleryImage = (idx) => {
    setF((s) => ({ ...s, gallery_urls: s.gallery_urls.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      await axios.put(`${API}/api/eticket/company`, {
        ...f,
        google_rating: Number(f.google_rating) || 0,
        google_review_count: parseInt(f.google_review_count, 10) || 0,
      });
      setMsg('Booking page saved.');
      onSaved && onSaved();
    } catch (e) { setMsg(e.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const setBadge = (k) => (e) =>
    setF((s) => ({ ...s, trust_badges: { ...s.trust_badges, [k]: e.target.checked } }));

  return (
    <div data-testid="booking-engine-settings" className="space-y-5 max-w-3xl">
      {/* Hero */}
      <Section icon={<Palette className="w-4 h-4" />} title="Hero Banner"
        sub="Large image + attraction name at the top of the public booking page.">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Hero image</span>
              <div className="mt-1 border border-dashed border-slate-300 rounded-xl p-3 flex flex-col items-center justify-center min-h-[120px] bg-slate-50">
                {(f.hero_image_url || fallbackHero) ? (
                  <div className="relative w-full">
                    <img src={f.hero_image_url || fallbackHero} alt="Hero"
                      className="w-full h-32 object-cover rounded-lg" data-testid="hero-preview" />
                    {f.hero_image_url ? (
                      <button type="button" onClick={() => setF((s) => ({ ...s, hero_image_url: '' }))}
                        className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow" data-testid="hero-remove"
                        title="Remove hero image (fall back to Banner image URL)">
                        <X className="w-3.5 h-3.5 text-red-600" />
                      </button>
                    ) : (
                      <span className="absolute bottom-1 left-1 bg-white/90 text-[10px] text-slate-600 rounded px-1.5 py-0.5">
                        Using Banner image URL
                      </span>
                    )}
                  </div>
                ) : (
                  <>
                    <ImageIcon className="w-6 h-6 text-slate-400" />
                    <p className="text-xs text-slate-500 mt-1">Recommended 1920×820 (21:9)</p>
                  </>
                )}
                <button type="button" onClick={() => heroRef.current?.click()} disabled={uploading === 'hero'}
                  data-testid="hero-upload-btn"
                  className="mt-2 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#0C2159] text-white hover:bg-[#13317a] disabled:opacity-60">
                  {uploading === 'hero' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {f.hero_image_url ? 'Replace image' : 'Upload image'}
                </button>
                <input ref={heroRef} type="file" accept="image/*" hidden onChange={onHeroPicked} data-testid="hero-file-input" />
              </div>
            </label>
            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
              💡 If no hero is uploaded, the public page will use your <strong>Banner image URL</strong> from Company Info.
              Uploaded hero always wins.
            </p>
          </div>
          <div className="space-y-3">
            <TextField label="Attraction name (headline)" v={f.hero_headline}
              on={(e) => setKey('hero_headline')(e.target.value)} testid="hero-headline" />
            <TextArea label="Short description" v={f.hero_subtext}
              on={(e) => setKey('hero_subtext')(e.target.value)} testid="hero-subtext" rows={3}
              placeholder="One or two sentences guests will read on the banner." />
          </div>
        </div>
      </Section>

      {/* Gallery */}
      <Section icon={<ImageIcon className="w-4 h-4" />} title="Image Gallery"
        sub="Show 3–10 photos beneath the banner. Hidden automatically when empty or disabled.">
        <div className="flex items-center gap-2 mb-3">
          <Toggle checked={f.gallery_enabled} onChange={(v) => setKey('gallery_enabled')(v)} testid="gallery-toggle" />
          <span className="text-sm text-slate-600">Show gallery on public page</span>
        </div>
        {/* Mobile gallery layout — desktop is always a horizontal row of thumbnails */}
        <div className="mb-4 border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="gallery-mobile-layout-row">
          <p className="text-xs font-semibold text-slate-700 mb-2">Mobile layout</p>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'carousel', label: 'Swipeable carousel', hint: 'Klook-style, one large photo at a time' },
              { id: 'grid', label: '2-column grid', hint: 'Two smaller photos per row' },
            ].map((opt) => {
              const active = f.gallery_mobile_layout === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setKey('gallery_mobile_layout')(opt.id)}
                  data-testid={`gallery-mobile-layout-${opt.id}`}
                  className={`text-left px-3 py-2 rounded-lg border text-xs flex-1 min-w-[160px] transition-colors ${
                    active ? 'border-[#0C2159] bg-white ring-1 ring-[#0C2159]' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className={`block font-semibold ${active ? 'text-[#0C2159]' : 'text-slate-700'}`}>{opt.label}</span>
                  <span className="block text-slate-400 mt-0.5">{opt.hint}</span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Applies to phones and small tablets only. The desktop layout is unchanged.</p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {f.gallery_urls.map((url, i) => (
            <div key={i} className="relative group" data-testid={`gallery-thumb-${i}`}>
              <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
              <button type="button" onClick={() => removeGalleryImage(i)} data-testid={`gallery-remove-${i}`}
                className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3 text-red-600" />
              </button>
            </div>
          ))}
          {f.gallery_urls.length < 10 && (
            <button type="button" onClick={() => galleryRef.current?.click()} disabled={!!uploading}
              data-testid="gallery-upload-btn"
              className="border-2 border-dashed border-slate-300 rounded-lg h-20 flex items-center justify-center text-slate-400 hover:border-[#0C2159] hover:text-[#0C2159] transition-colors">
              {uploading === 'gallery' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            </button>
          )}
        </div>
        <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={onGalleryPicked} data-testid="gallery-file-input" />
        <p className="text-xs text-slate-400 mt-2">{f.gallery_urls.length}/10 images · JPG, PNG or WebP · Max 8MB each</p>
      </Section>

      {/* Attraction Info */}
      <Section icon={<Info className="w-4 h-4" />} title="Attraction Information"
        sub="Location, opening hours, and estimated visit duration.">
        <div className="grid sm:grid-cols-3 gap-3">
          <TextField label={<><MapPin className="w-3 h-3 inline mr-1" />Location / Address</>} v={f.location}
            on={(e) => setKey('location')(e.target.value)} testid="info-location"
            placeholder="e.g. 15 Lebuh Pantai, George Town, Penang" />
          <TextField label={<><Clock className="w-3 h-3 inline mr-1" />Opening hours</>} v={f.opening_hours}
            on={(e) => setKey('opening_hours')(e.target.value)} testid="info-hours"
            placeholder="e.g. Mon–Sun · 9:00 AM – 6:00 PM" />
          <TextField label={<><Timer className="w-3 h-3 inline mr-1" />Estimated visit duration (hours)</>} v={f.visit_duration}
            on={(e) => setKey('visit_duration')(e.target.value)} testid="info-duration"
            placeholder="e.g. 2 (shows as '2 hours' — or type '1–2 hours' for a range)" />
        </div>
      </Section>

      {/* Trust Badges */}
      <Section icon={<ShieldCheck className="w-4 h-4" />} title="Trust Badges"
        sub="Toggle which badges appear on the booking page.">
        <div className="grid sm:grid-cols-3 gap-3">
          <BadgeToggle label="Instant Confirmation" checked={f.trust_badges.instant_confirmation}
            onChange={setBadge('instant_confirmation')} testid="badge-instant" />
          <BadgeToggle label="Secure Payment" checked={f.trust_badges.secure_payment}
            onChange={setBadge('secure_payment')} testid="badge-secure" />
          <BadgeToggle label="Mobile QR Ticket" checked={f.trust_badges.mobile_qr}
            onChange={setBadge('mobile_qr')} testid="badge-qr" />
        </div>
      </Section>

      {/* Footer */}
      <Section icon={<Link2 className="w-4 h-4" />} title="Footer" sub="Links and contact shown at the bottom of the page.">
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Privacy Policy URL" v={f.footer_privacy_url}
            on={(e) => setKey('footer_privacy_url')(e.target.value)} testid="footer-privacy" placeholder="https://…" />
          <TextField label="Terms & Conditions URL" v={f.footer_terms_url}
            on={(e) => setKey('footer_terms_url')(e.target.value)} testid="footer-terms" placeholder="https://…" />
          <TextField label="Support Email" v={f.support_email}
            on={(e) => setKey('support_email')(e.target.value)} testid="footer-email" placeholder="support@example.com" />
          <TextField label="Support Phone" v={f.support_phone}
            on={(e) => setKey('support_phone')(e.target.value)} testid="footer-phone" placeholder="+60 …" />
        </div>
      </Section>

      <Section icon={<Star className="w-4 h-4" />} title="Google rating" sub="Shown under the page title. Use your real Google Business rating. Leave blank to hide.">
        <div className="grid sm:grid-cols-3 gap-3">
          <TextField label="Rating (e.g. 4.8)" v={f.google_rating}
            on={(e) => setKey('google_rating')(e.target.value)} testid="google-rating" placeholder="4.8" />
          <TextField label="Number of reviews" v={f.google_review_count}
            on={(e) => setKey('google_review_count')(e.target.value)} testid="google-review-count" placeholder="120" />
          <TextField label="Google Maps link" v={f.google_maps_url}
            on={(e) => setKey('google_maps_url')(e.target.value)} testid="google-maps-url" placeholder="https://maps.app.goo.gl/…" />
        </div>
      </Section>

      <Section icon={<ShieldCheck className="w-4 h-4" />} title="Ticket seller" sub="Shown in Provider information on the booking page. Leave blank to hide.">
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Seller company name" v={f.ticket_seller_name}
            on={(e) => setKey('ticket_seller_name')(e.target.value)} testid="ticket-seller-name" placeholder="Company Sdn Bhd" />
          <TextField label="Seller reg. no." v={f.ticket_seller_reg_no}
            on={(e) => setKey('ticket_seller_reg_no')(e.target.value)} testid="ticket-seller-reg" placeholder="202601028203 (1690298-K)" />
        </div>
      </Section>

      {msg && <p className="text-sm text-slate-600" data-testid="booking-engine-msg">{msg}</p>}
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} data-testid="booking-engine-save"
          className="bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save booking page
        </button>
        {company.slug && (
          <a href={`/eticket/${company.slug}`} target="_blank" rel="noopener noreferrer"
            className="text-sm text-[#0C2159] underline" data-testid="booking-engine-preview">
            Preview public page →
          </a>
        )}
      </div>
    </div>
  );
}

const Section = ({ icon, title, sub, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6">
    <div className="flex items-center gap-2 mb-1">
      <span className="text-[#0C2159]">{icon}</span>
      <h3 className="font-bold text-slate-900">{title}</h3>
    </div>
    {sub && <p className="text-xs text-slate-500 mb-4">{sub}</p>}
    {children}
  </div>
);

const TextField = ({ label, v, on, testid, placeholder, type = 'text' }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-600">{label}</span>
    <input data-testid={testid} type={type} value={v || ''} onChange={on} placeholder={placeholder}
      className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0C2159]" />
  </label>
);

const TextArea = ({ label, v, on, testid, placeholder, rows = 3 }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-600">{label}</span>
    <textarea data-testid={testid} value={v || ''} onChange={on} placeholder={placeholder} rows={rows}
      className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0C2159] resize-y" />
  </label>
);

const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-slate-300'}`}>
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : 'translate-x-1'}`} />
  </button>
);

const BadgeToggle = ({ label, checked, onChange, testid }) => (
  <label className={`flex items-center gap-2 border rounded-xl px-3 py-2.5 cursor-pointer transition-colors ${checked ? 'border-[#0C2159] bg-[#0C2159]/5' : 'border-slate-200 hover:bg-slate-50'}`}>
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid}
      className="h-4 w-4 rounded border-slate-300 text-[#0C2159] focus:ring-[#0C2159]" />
    <span className="text-sm text-slate-800">{label}</span>
  </label>
);
